import { CaretUpDownIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowDown, ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useEventProducts,
  useEventStations,
  useExportAuditEntries,
} from '@/modules/events/api';
import { AuditReportType } from '@/modules/events/constants';
import {
  EventProduct,
  EventProductResponseItem,
  Size,
} from '@/modules/events/types';

import { SizeRow } from '..';
import { InventoryAmountInput, InventoryRemoveButton } from '../components';

export const useInventoryTab = () => {
  const params = useParams<{ id: string }>();
  const eventId = params.id;

  const queryClient = useQueryClient();
  const { getValues } = useFormContext();

  const [isUploading, setIsUploading] = useState(false);
  const [selectedStationId, setSelectedStationId] = useState<string>('');

  const { stationsQuery } = useEventStations(eventId, true);

  const selectedStation = stationsQuery.data.find(
    (station) => station.id === selectedStationId
  );

  const { exportAudit, activeAuditType } = useExportAuditEntries({
    eventId,
    eventName: getValues('name'),
    stationId: selectedStationId,
    stationName: selectedStation?.name,
  });

  const {
    isFetching,
    products,
    uploadEventProducts,
    getGenerateReportUrl,
    isGeneratingReport,
    restTableProps,
  } = useEventProducts(eventId, selectedStationId);

  const uploadProducts = async (file: File) => {
    setIsUploading(true);
    try {
      await uploadEventProducts(file);
      restTableProps.refetch();
      queryClient.invalidateQueries({
        queryKey: ['eventDetails', String(eventId)],
      });
      toast.success('Inventory uploaded successfully');
      setIsUploading(false);
    } finally {
      setIsUploading(false);
    }
  };

  const pickFileAndUpload = async (event) => {
    if (!event.target.files.length) return;

    const file = event.target.files[0];

    await uploadProducts(file);
  };

  useEffect(() => {
    if (!stationsQuery.data) {
      return;
    }

    if (!stationsQuery.data.length) {
      return;
    }

    setSelectedStationId(stationsQuery.data[0].id);
  }, [stationsQuery.data]);

  const columns: ColumnDef<EventProductResponseItem>[] = [
    {
      id: 'expander',

      header: ({ table }) => {
        const allExpanded =
          Object.keys(table.getState().expanded).length ===
          table.getRowModel().rows.length;
        const toggleAll = (e) => {
          e.stopPropagation();
          const newExpanded: Record<string, boolean> = {};
          if (!allExpanded) {
            table.getRowModel().rows.forEach((row) => {
              newExpanded[row.id] = true;
            });
          }
          table.setExpanded(newExpanded);
        };

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="flex items-center gap-1 px-3 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-sm"
                onClick={toggleAll}
              >
                {allExpanded ? <ChevronUp /> : <ChevronDown />}
              </Button>
            </TooltipTrigger>
            <TooltipContent style={{ textTransform: 'none' }}>
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </TooltipContent>
          </Tooltip>
        );
      },
      size: 40,
      cell: ({ row }) => (
        <Button
          type="button"
          size="sm"
          variant="transparent"
          onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
        >
          {row.getIsExpanded() ? <ChevronUp /> : <ChevronDown />}
        </Button>
      ),
    },
    {
      size: 200,
      accessorKey: 'category',
      header: ({ column }) => {
        return (
          <Button
            type="button"
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Category
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.category}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            type="button"
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-2">{row.original.name}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'nickname',
      header: ({ column }) => {
        return (
          <Button
            type="button"
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Nickname
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.nickname}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'styleNumber',
      header: 'Style Number',
    },
    {
      size: 200,
      accessorKey: 'totalAmount',
      header: 'Total Amount',
    },
    {
      size: 40,
      id: 'actions',
      header: 'Action',
      enableHiding: false,
      cell: ({ row }) => (
        <InventoryRemoveButton
          eventId={eventId}
          stationId={selectedStationId}
          productId={row.original.id}
          onSuccess={() => restTableProps.refetch()}
        />
      ),
    },
  ];

  const subRowsColumns: ColumnDef<SizeRow>[] = [
    {
      accessorKey: 'size',
      header: 'Size',
      size: 400,
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.size}</span>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',

      cell: ({ row }) => {
        return (
          <InventoryAmountInput
            eventId={eventId}
            stationId={selectedStationId}
            productId={row.original.parent.id}
            sizeId={row.original?.id}
            defaultValue={row.original.amount.toString()}
            onSuccess={() => restTableProps.refetch()}
          />
        );
      },
    },
    {
      id: 'action',
      header: 'Action',
      enableHiding: false,
      size: 80,
      cell: ({ row }) => (
        <InventoryRemoveButton
          eventId={eventId}
          stationId={selectedStationId}
          productId={row.original.parent.id}
          sizeId={row.original?.id}
          isLastSize={row.original.parent.sizes.length === 1}
          onSuccess={() => restTableProps.refetch()}
        />
      ),
    },
  ];

  return {
    eventId,
    columns,
    subRowsColumns,
    products,
    isFetching,
    isUploading,
    isGeneratingReport,
    stationsQuery,
    selectedStationId,
    restTableProps,
    isExportingStationAudit: activeAuditType === AuditReportType.Station,
    isExportingProductsAudit: activeAuditType === AuditReportType.Products,
    getGenerateReportUrl,
    pickFileAndUpload,
    setSelectedStationId,
    exportStationAudit: () => exportAudit(AuditReportType.Station),
    exportProductsAudit: () => exportAudit(AuditReportType.Products),
  };
};
